// THE STRUCTURES ON THE POOL (R8c2): the structures.bin decode, the
// structure collider's index and the structure draw's arrays, off the main
// thread (client/src/pool.js; the ops registered in poolwork.js). PURE: no
// three, no DOM — the worker bundle imports this module, and so does the
// main thread (structs2.js wraps the draw arrays, main.js restores the
// collider), so both sides run ONE copy of every function here.
//
//   'structures'       { buf (structures.bin, transferred), opt (the
//                      collider's { slopeWalkDeg }) } -> { rec, col, draw }:
//                      the reader's record (game/structures.js
//                      loadStructures), the collider's index (game/structcol.js
//                      StructCollider.build, drained) and a second copy of
//                      the record (made here: the main thread keeps `rec` for
//                      the collider and MOVES `draw` into the draw job, never
//                      copying 10 MB itself), typed arrays moved back
//   'structures.draw'  { rec (that copy, transferred), dd ({ rails, bores }
//                      of ways.json or null), ground (structures_ground.bin
//                      or null, transferred), steps (wayslook "steps" or
//                      null) } ->
//                      the draw's arrays (structDrawArrays) + groundErr (the
//                      ground file's decode failure: drawn without it)
//
// Two jobs, not one: the collider is ground (decks, bridges) the body needs
// as soon as structures.bin is in; the draw waits for the ways doc's rails
// and the ground file as before (main.js).
//
// restoreStructures / restoreCollider (main thread) rebuild the record and
// the collider from the op's arrays: the same fields in the same order as a
// main-thread loadStructures + StructCollider.build, nothing recomputed
// (scripts/check-structcol.mjs proves field for field, and the draw arrays
// byte for byte against the pre-R8c2 StructDraw.build).
import { TF, SRC, KINDS, loadStructures } from '../../game/structures.js';
import { StructCollider } from '../../game/structcol.js';
import { MAT_DRAW, DRAW, kindRow, matRow, batchOf, chunkPlan, boreCoord, boreLight, TIE_TRIS, farContacts, farBodies, loadGround } from './structplan.js';
import { STONE_MAT_IDX, STONE_RGB, SW, wallCoords, steepTri } from './stonewall.js';
import { railingGeom } from './railing.js';

// ------------------------------------------------ the record and the collider

// the reader's record -> the op's form: its typed arrays as they are (verts /
// idx / mat / tflags views of the file's one buffer: a post keeps them views
// of one buffer at the same offsets), the chunk table as numbers
export function packStructures(str) {
  const C = new Float64Array(str.chunks.length * 6);
  str.chunks.forEach((c, k) => { C.set([c.cx, c.cz, c.first, c.count, c.firstTri, c.nTris], k * 6); });
  return {
    version: str.version, chunk: str.chunk, nStruct: str.nStruct, nVerts: str.nVerts, nTris: str.nTris, chunks: C,
    kind: str.kind, prov: str.prov, srcKind: str.srcKind, srcIndex: str.srcIndex, firstVert: str.firstVert, nVert: str.nVert,
    firstTri: str.firstTri, nTri: str.nTri, bbox: str.bbox, verts: str.verts, idx: str.idx, mat: str.mat, tflags: str.tflags, bytes: str.bytes,
  };
}
// the op's form -> the reader's record: game/structures.js loadSteps' return,
// key for key (structOf: the same binary search over firstTri)
export function restoreStructures(r) {
  const { nStruct, firstTri } = r, chunks = [];
  for (let k = 0; k < r.chunks.length; k += 6) {
    const C = r.chunks;
    chunks.push({ cx: C[k], cz: C[k + 1], first: C[k + 2], count: C[k + 3], firstTri: C[k + 4], nTris: C[k + 5] });
  }
  return {
    version: r.version, chunk: r.chunk, nStruct, nVerts: r.nVerts, nTris: r.nTris, chunks,
    kind: r.kind, prov: r.prov, srcKind: r.srcKind, srcIndex: r.srcIndex, firstVert: r.firstVert, nVert: r.nVert, firstTri, nTri: r.nTri, bbox: r.bbox,
    verts: r.verts, idx: r.idx, mat: r.mat, tflags: r.tflags,
    bytes: r.bytes,
    // structure owning triangle t (binary search over firstTri)
    structOf(t) {
      let lo = 0, hi = nStruct - 1;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (firstTri[mid] <= t) lo = mid; else hi = mid - 1;
      }
      return lo;
    },
  };
}

// the collider fields StructCollider._init derives from the record (the
// index); every other field is its query scratch, the same for any record
const COL_INDEX = ['cell', 'nTris', 'ymin', 'ymax', 'role', 'nColl', '_mask', '_shift', 'hkey', 'hoff', 'hcnt', 'cellTris', 'nCells', 'nRefs', 'maxCell'];
export function packCollider(c) {
  const o = {};
  for (const k of COL_INDEX) o[k] = c[k];
  return o;
}
// the collider over `str` from its packed index: StructCollider.prototype,
// its fields in _init's order — the record's (str, V, I), the index, a fresh
// stamp (all zero after a build), and the scratch of an EMPTY collider built
// with the same options (game/structcol.js read-only: its own _init sets the
// scratch, no copy of its sizes here)
export function restoreCollider(str, col, opt = {}) {
  const tmpl = new StructCollider(null, opt);
  const own = { ...col, str, V: str.verts, I: str.idx, stamp: new Uint16Array(str.nTris) };
  const c = Object.create(StructCollider.prototype);
  for (const k of Object.keys(tmpl)) c[k] = Object.prototype.hasOwnProperty.call(own, k) ? own[k] : tmpl[k];
  return c;
}

// the transferables under v (typed arrays' buffers; plain objects / arrays
// walked) — a post's transfer list: transferOf(v)
function buffersOf(v, out = new Set()) {
  if (v instanceof ArrayBuffer) out.add(v);
  else if (ArrayBuffer.isView(v)) out.add(v.buffer);
  else if (Array.isArray(v)) for (const x of v) buffersOf(x, out);
  else if (v && typeof v === 'object') for (const k in v) buffersOf(v[k], out);
  return out;
}
export const transferOf = (v) => [...buffersOf(v)];

// op 'structures': decode, the collider's index (structcol's build, drained)
export function structuresOp({ buf, opt }) {
  const str = loadStructures(buf);
  const it = StructCollider.build(str, opt || {});
  let s;
  do s = it.next(); while (!s.done);
  const rec = packStructures(str);
  // the draw job's copy: the same views of one (copied) file buffer
  const result = { rec, col: packCollider(s.value), draw: structuredClone(rec) };
  return { result, transfer: transferOf(result) };
}

// op 'structures.draw': the draw's arrays over the record (+ the ground file)
export function structuresDrawOp({ rec, dd, ground, steps }) {
  const str = restoreStructures(rec);
  let g = null, groundErr = null;
  if (ground) {
    try { g = loadGround(ground, str.nVerts); } catch (e) { groundErr = e.message; }
  }
  const result = structDrawArrays(str, { ...(dd || {}), ground: g }, steps || null);
  result.groundErr = groundErr;
  return { result, transfer: transferOf(result) };
}

export const STRUCT_OPS = { structures: structuresOp, 'structures.draw': structuresDrawOp };

// ------------------------------------------------ three's colour / sphere maths
// EXACT copies of three r170 (scripts/check-structcol.mjs proves equality
// against THREE.Color / BufferGeometry.computeBoundingSphere / Sphere):
// the worker bundle carries no three.

// ColorManagement SRGBToLinear (math/ColorManagement.js)
const srgbLin = (c) => ((c < 0.04045) ? c * 0.0773993808 : Math.pow(c * 0.9478672986 + 0.0521327014, 2.4));
// new THREE.Color().setHex(hex) (sRGB -> the linear working space) -> [r, g, b]
export function hexLinear(hex) {
  hex = Math.floor(hex);
  return [srgbLin((hex >> 16 & 255) / 255), srgbLin((hex >> 8 & 255) / 255), srgbLin((hex & 255) / 255)];
}
// THREE.Color.setRGB(r, g, b, SRGBColorSpace) -> [r, g, b] linear
export const srgbRgbLinear = (r, g, b) => [srgbLin(r), srgbLin(g), srgbLin(b)];
// BufferGeometry.computeBoundingSphere over a position array (itemSize 3,
// not normalized, no morphs) -> [cx, cy, cz, r]: Box3.setFromBufferAttribute,
// its centre (0 when empty), the largest squared distance
export function sphereOf(P) {
  const n = P.length / 3;
  let x0 = +Infinity, y0 = +Infinity, z0 = +Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
  for (let i = 0; i < n; i++) {
    const x = P[i * 3], y = P[i * 3 + 1], z = P[i * 3 + 2];
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); z0 = Math.min(z0, z);
    x1 = Math.max(x1, x); y1 = Math.max(y1, y); z1 = Math.max(z1, z);
  }
  const S = new Float64Array(4);
  if (!((x1 < x0) || (y1 < y0) || (z1 < z0))) { S[0] = (x0 + x1) * 0.5; S[1] = (y0 + y1) * 0.5; S[2] = (z0 + z1) * 0.5; }
  let m = 0;
  for (let i = 0; i < n; i++) {
    const dx = S[0] - P[i * 3], dy = S[1] - P[i * 3 + 1], dz = S[2] - P[i * 3 + 2];
    m = Math.max(m, dx * dx + dy * dy + dz * dz);
  }
  S[3] = Math.sqrt(m);
  return S;
}
// Sphere.expandByPoint on S = [cx, cy, cz, r]
export function expandSphere(S, x, y, z) {
  if (S[3] < 0) { S[0] = x; S[1] = y; S[2] = z; S[3] = 0; return S; }
  const vx = x - S[0], vy = y - S[1], vz = z - S[2];
  const lengthSq = vx * vx + vy * vy + vz * vz;
  if (lengthSq > (S[3] * S[3])) {
    const length = Math.sqrt(lengthSq);
    const delta = (length - S[3]) * 0.5;
    const s = delta / length;
    S[0] += vx * s; S[1] += vy * s; S[2] += vz * s;
    S[3] += delta;
  }
  return S;
}

// ------------------------------------------------ the draw's arrays
// (moved from structs2.js StructDraw.build, R8c2: the same code in the same
// order; the THREE objects are structs2.js StructDraw.wrap's)

const SRC_BORES = SRC.indexOf('bores');
// (P3d) a light strip in the lit program: a src-sites triangle with TF.LIGHT gets stair code 100
// (0.39: neither a nosing > 0.5 nor plain 0) and the warm-white lamp colour; the program draws it
// unlit at DRAW.fixture_glow (MeshLambert's own colour path, no scene light; tone-mapped like the
// bore luminaires: scene radiance, APP_SURFACE §Light)
const SRC_SITES = SRC.indexOf('sites'), LIGHT_CODE = 100;
// (fill stone, owner 2026-10-16: ONE stone everywhere) a lit masonry / stone triangle (stonewall.js
// STONE_MATS; not a stair, not a light strip) gets stair code 200 (0.78: over the light strip, under a
// nosing > 0.9), the stone's mean as its vertex colour (STONE_RGB) and, on a wall face, its corners'
// fitted (v, H) in uv (stonewall.js wallCoords; a top: uv 0, 0): the lit program draws the procedural
// stone instead of the speckle. The pattern's mean is 1, so the drawn mean is the masonry albedo.
export const STONE_CODE = 200;
const ZERO_UV = [0, 0, 0, 0, 0, 0];
// (D1-TUNC) an UNTEXTURED bore part (rails, sleepers, luminaires) in the
// textured bore program: per triangle uv (0,0) (FLAT_UV,0) (0,FLAT_UV) —
// derivatives so large the sampler takes the boreTexture's coarsest mip,
// its mean (DRAW.bore_tex_mean): the vertex colour / that mean is the colour
const FLAT_UV = 1e7;
const FLAT_U = [0, FLAT_UV, 0], FLAT_V = [0, 0, FLAT_UV];
export const TIE_V = TIE_TRIS * 3;       // vertices per bore sleeper
const _E = [0, 0, 0];
// (D1-TUNC R2) the grate stair byte of a bore-lit grate triangle (structs2.js grate())
export const GRATE_BORE_STAIR = 170, GRATE_BORE_TRACK = 85;
const _cols = new Array(9).fill(0);
// an sRGB hex as linear rgb
function linOf(hex) { return hexLinear(hex); }
// (D1-TUNC) a bore sleeper's long faces against its top (no normals in the bore program)
const TIE_SIDE = 0.7;
// the ground sleepers' weathering (client/src/ways2.js jit): brightness x
// [1 - a, 1] from a hash of the position
function jit(x, z, a) {
  const h = Math.sin(Math.round(x * 10) * 12.9898 + Math.round(z * 10) * 78.233) * 43758.5453;
  return 1 - a * (h - Math.floor(h));
}
// the bore rails' linear colours (ways2.js railUnit): running top, head side
// (top lerped 0.55 to the foot), weathered foot (THREE.Color(hex), clone().lerp)
function railColours() {
  const top = hexLinear(DRAW.rail_top_rgb.v), foot = hexLinear(DRAW.rail_foot_rgb.v);
  const side = top.map((v, k) => v + (foot[k] - v) * 0.55);
  return { top, side, foot };
}

export function srgbToLinear(v) {
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

// a MAT_DRAW row's lit colour as linear-space bytes over the detail mean
// (vertex colours are linear in three; the palette hex is sRGB)
function rgbOf(row, mean) {
  return hexLinear(row.color).map((v) => Math.min(255, Math.round((v / mean) * 255)));   // sRGB hex -> linear working colour
}

// one rail segment (run r, segment i): two rails, three faces each (outer
// side, head, inner side) as quads -> emit(tri, face, ring, end) per
// triangle (ring: each corner's ring index 0..3, 0 / 3 the bottom; end: 0
// at point i, 1 at i + 1). top / bot: the section's half-widths at the
// head and at the bottom (default DRAW.rail_w_m both: the deck rails'
// box; (D1-TUNC) a bore rail's trapezoid)
function railSeg(r, i, emit, top = DRAW.rail_w_m.v, bot = top) {
  const P = r.pts, a = P[i], b = P[i + 1];
  const perp = (k) => {
    const q = P[Math.min(P.length - 1, k + 1)], o = P[Math.max(0, k - 1)];
    const dx = q[0] - o[0], dz = q[2] - o[2], l = Math.hypot(dx, dz) || 1;
    return [-dz / l, dx / l];
  };
  const na = perp(i), nb = perp(i + 1);
  const RH = DRAW.rail_h_m.v;
  for (const side of [-1, 1]) {
    const off = side * r.gauge / 2;
    const ring = (p, n) => {
      const cx = p[0] + n[0] * off, cz = p[2] + n[1] * off, y = p[1];
      return [[cx + n[0] * bot, y - RH, cz + n[1] * bot], [cx + n[0] * top, y, cz + n[1] * top],
        [cx - n[0] * top, y, cz - n[1] * top], [cx - n[0] * bot, y - RH, cz - n[1] * bot]];
    };
    const A = ring(a, na), Bq = ring(b, nb);
    for (let f = 0; f < 3; f++) {
      emit([A[f], Bq[f], A[f + 1]], f, [f, f, f + 1], [0, 1, 0]);
      emit([A[f + 1], Bq[f], Bq[f + 1]], f, [f + 1, f, f + 1], [0, 1, 1]);
    }
  }
}

// a draw group's near-sleeper index (structplan tieIndex: p.tieCells, a Map
// 'i,j' -> sleeper list, in insertion order) as arrays
function packTieCells(cells) {
  const ij = new Int32Array(cells.size * 2), off = new Uint32Array(cells.size + 1);
  let n = 0, k = 0;
  for (const L of cells.values()) n += L.length;
  const list = new Uint32Array(n);
  n = 0;
  for (const [key, L] of cells) {
    const c = key.indexOf(',');
    ij[k * 2] = Number(key.slice(0, c)); ij[k * 2 + 1] = Number(key.slice(c + 1));
    off[k] = n;
    list.set(L, n); n += L.length; k++;
  }
  off[k] = n;
  return { ij, off, list };
}
export function unpackTieCells({ ij, off, list }) {
  const cells = new Map();
  for (let k = 0; k < off.length - 1; k++) cells.set(ij[k * 2] + ',' + ij[k * 2 + 1], Array.from(list.subarray(off[k], off[k + 1])));
  return cells;
}

// str: the loadStructures record the collider uses; dd: {rails, bores}
// from ways.json (null: no rails, lamp shading at its mean) + ground (Float32
// T0 - y per vertex, FAR_RULE); steps: (A5) L's stair look or null.
// -> { groups: [{ cx, cz, x, z, r, n, nHead, draws, L, B, G, ts, foot }],
// triMesh, triVert, stats }: per group its lit / bore / grate batch's arrays
// (L / G: pos, nor, col, uv, st; B: pos, col, uv; each n tris, its head
// (vertices before the thin tail) and bs (its bounding sphere: the bore's
// grown by its sleeper block)), the sleeper block, the far-rule contacts.
// Throws what StructDraw.build threw (a kind / material without a row, a
// batch that does not fill to its plan).
export function structDrawArrays(str, dd, steps = null) {
  const rails = (dd && dd.rails) || [];
  const frames = (dd && dd.bores) || [];
  const ground = (dd && dd.ground) || null;   // Float32 T0 - y per vertex (FAR_RULE)
  const plan = chunkPlan(str, rails);
  const triMesh = new Int32Array(str.nTris).fill(-1), triVert = new Int32Array(str.nTris).fill(-1);
  const W = ground ? farContacts(str, ground) : null;   // FAR_RULE weight per vertex, 0 = static
  const Bd = ground ? farBodies(str, ground) : null;    // FAR BODY RULE: 1 = moves whole past view_m
  let nFoot = 0, nBody = 0;
  const { verts, idx, mat, tflags } = str;
  const mean = srgbToLinear(DRAW.detail_mean.v), dm = DRAW.detail_m.v, bm = DRAW.bore_uv_m.v;
  const lit = MAT_DRAW;
  let nLit = 0, nBore = 0, nGrate = 0, nTie = 0, nGrateBore = 0, nMesh = 0, tieBlock = 0;
  const groups = [];
  const KF = KINDS.indexOf('funicular-stair');
  // (D1-TUNC) the bore frames the rail extras were measured on (railRuns
  // .bores, the same boreFrames(doc) as dd.bores), the sleepers, the
  // untextured bore parts' colour scale: bore_obj_k / the texture mean
  const boreF = rails.bores || frames, tieSrc = rails.ties || { t: new Float64Array(0), f: new Int32Array(0) };
  const BM = DRAW.bore_tex_mean.v, OK = DRAW.bore_obj_k.v / BM;
  const RC = railColours(), TIE_C = DRAW.tie_rgb.v.map((v) => srgbToLinear(v / 255));
  const E0 = [DRAW.lamp_flat.v, DRAW.lamp_flat.v, DRAW.lamp_flat.v];
  // the bore light at rail point q of entry r (its frame entry.bf[q])
  const railLight = (r, q) => {
    const k = r.bf ? r.bf[q] : -1, f = k >= 0 ? boreF[k] : null;
    if (!f) return E0;
    const P3 = r.pts[q], c = boreCoord(f, P3[0], P3[1], P3[2]);
    return boreLight(f, c.s, c.up, [0, 0, 0]);
  };
  // (A5) L's tread colour for the raised flights' STAIR triangles
  const a5 = steps ? srgbRgbLinear(...steps.colour.tread.map((v) => v / 255)).map((v) => Math.min(255, Math.round((v / mean) * 255))) : null;
  for (const p of plan.values()) {
    const nl = p.tris.lit, nb = p.tris.bore, ng = p.tris.grate;
    const mkL = (n) => (n ? { pos: new Float32Array(n * 9), nor: new Float32Array(n * 9), col: new Uint8Array(n * 9), uv: new Float32Array(n * 6), st: new Uint8Array(n * 3), o: 0 } : null);
    const L = mkL(nl), Gr = mkL(ng);
    // the bore batch: [head | thin tail | (D1-TUNC) the near-sleeper block:
    // tieCap sleepers' vertices, filled by update() within tie_view_m]
    const nTV = p.tieCap * TIE_V;
    const B = nb || nTV ? { pos: new Float32Array(nb * 9 + nTV * 3), col: new Float32Array(nb * 9 + nTV * 3), uv: new Float32Array(nb * 6 + nTV * 2), o: 0 } : null;
    // FAR_RULE contacts of this chunk: unique source vertex -> uid, and per
    // batch the (output vertex, uid) pairs to rewrite
    const fuid = new Map(), fsrc = [], fL = [], fB = [], fG = [], gt = [];
    const foot = (v, out, list) => {
      let u = fuid.get(v);
      if (u === undefined) { u = fsrc.length; fuid.set(v, u); fsrc.push(v); }
      list.push(out, u);
    };
    const contact = (v) => W !== null && (W[v] > 0 || Bd[v] === 1);
    // cols (optional): 9 per-vertex colour bytes instead of rgb; code
    // (optional): the stair byte instead of stair ? 255 : 0; uvs (optional):
    // 6 per-vertex uv values instead of the box mapping (the stone's (v, H))
    const litTri = (L, ax, ay, az, bx, by, bz, cx, cy, cz, rgb, stair, cols = null, code = -1, uvs = null) => {
      const ux = bx - ax, uy = by - ay, uz = bz - az, vx = cx - ax, vy = cy - ay, vz = cz - az;
      let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
      const l = Math.hypot(nx, ny, nz) || 1;
      nx /= l; ny /= l; nz /= l;
      // box mapping by the dominant normal axis
      const X = Math.abs(nx), Y = Math.abs(ny), Z = Math.abs(nz);
      const P3 = [ax, ay, az, bx, by, bz, cx, cy, cz];
      for (let k = 0; k < 3; k++) {
        const o = L.o + k;
        const x = P3[k * 3], y = P3[k * 3 + 1], z = P3[k * 3 + 2];
        L.pos[o * 3] = x; L.pos[o * 3 + 1] = y; L.pos[o * 3 + 2] = z;
        L.nor[o * 3] = nx; L.nor[o * 3 + 1] = ny; L.nor[o * 3 + 2] = nz;
        if (cols) { L.col[o * 3] = cols[k * 3]; L.col[o * 3 + 1] = cols[k * 3 + 1]; L.col[o * 3 + 2] = cols[k * 3 + 2]; }
        else { L.col[o * 3] = rgb[0]; L.col[o * 3 + 1] = rgb[1]; L.col[o * 3 + 2] = rgb[2]; }
        if (uvs) { L.uv[o * 2] = uvs[k * 2]; L.uv[o * 2 + 1] = uvs[k * 2 + 1]; }
        else {
          const u = Y >= X && Y >= Z ? x : (X >= Z ? z : x);
          const v = Y >= X && Y >= Z ? z : y;
          L.uv[o * 2] = u / dm; L.uv[o * 2 + 1] = v / dm;
        }
        L.st[o] = code >= 0 ? code : stair ? 255 : 0;
      }
      L.o += 3;
    };
    // cache: one bore measurement per SOURCE vertex (a vertex is shared by
    // ~3 triangles of its record): u, v and THE BORE LIGHT E (rgb,
    // structplan boreLight: max(portal daylight, lamp pools x the bore's
    // lamp colour)); no bore record -> DRAW.lamp_flat, neutral
    const boreVert = (x, y, z, tint, f, cache, vi) => {
      const o = B.o;
      B.pos[o * 3] = x; B.pos[o * 3 + 1] = y; B.pos[o * 3 + 2] = z;
      let u, v, er, eg, eb;
      if (f) {
        if (cache && cache[vi * 5 + 4] === cache[vi * 5 + 4]) {
          u = cache[vi * 5]; v = cache[vi * 5 + 1]; er = cache[vi * 5 + 2]; eg = cache[vi * 5 + 3]; eb = cache[vi * 5 + 4];
        } else {
          const c = boreCoord(f, x, y, z);
          boreLight(f, c.s, c.up, _E);
          u = (c.lat + c.up) / bm; v = c.s / bm; er = _E[0]; eg = _E[1]; eb = _E[2];
          if (cache) { cache[vi * 5] = u; cache[vi * 5 + 1] = v; cache[vi * 5 + 2] = er; cache[vi * 5 + 3] = eg; cache[vi * 5 + 4] = eb; }
        }
      } else {
        u = (x + y) / bm; v = z / bm; er = eg = eb = DRAW.lamp_flat.v;
      }
      B.col[o * 3] = tint[0] * er; B.col[o * 3 + 1] = tint[1] * eg; B.col[o * 3 + 2] = tint[2] * eb;
      B.uv[o * 2] = u; B.uv[o * 2 + 1] = v;
      B.o++;
    };
    // (D1-TUNC R2) the bore light E at a record vertex (the same cache as
    // boreVert: u, v, E per source vertex) -> _E
    const boreE = (x, y, z, f, cache, vi) => {
      if (cache && cache[vi * 5 + 4] === cache[vi * 5 + 4]) {
        _E[0] = cache[vi * 5 + 2]; _E[1] = cache[vi * 5 + 3]; _E[2] = cache[vi * 5 + 4];
        return _E;
      }
      const c = boreCoord(f, x, y, z);
      boreLight(f, c.s, c.up, _E);
      if (cache) { cache[vi * 5] = (c.lat + c.up) / bm; cache[vi * 5 + 1] = c.s / bm; cache[vi * 5 + 2] = _E[0]; cache[vi * 5 + 3] = _E[1]; cache[vi * 5 + 4] = _E[2]; }
      return _E;
    };
    // (D1-TUNC) an untextured bore part's vertex: colour = linear albedo x
    // E (/ the texture mean, the flat uv's mip), k = its corner in the
    // triangle (the flat uv); into Q = B or the tie store
    const flatVert = (Q, x, y, z, r, g, b, k) => {
      const o = Q.o;
      Q.pos[o * 3] = x; Q.pos[o * 3 + 1] = y; Q.pos[o * 3 + 2] = z;
      Q.col[o * 3] = r; Q.col[o * 3 + 1] = g; Q.col[o * 3 + 2] = b;
      if (Q.uv) { Q.uv[o * 2] = FLAT_U[k]; Q.uv[o * 2 + 1] = FLAT_V[k]; }
      Q.o++;
    };
    const quad = (Q, a, b, c, d, ca, cb, cc, cd) => {   // a b c d around; colours per corner
      flatVert(Q, ...a, ...ca, 0); flatVert(Q, ...b, ...cb, 1); flatVert(Q, ...c, ...cc, 2);
      flatVert(Q, ...a, ...ca, 0); flatVert(Q, ...c, ...cc, 1); flatVert(Q, ...d, ...cd, 2);
    };
    // structures of this draw group's STR1 chunks — THE THIN-KIND VIEW
    // RULE (structplan DRAW.thin_view_m): the thin kinds' records last, so
    // each batch is [head | thin tail] and beyond thin_view_m it draws its
    // head only (drawRange, no extra draw call)
    const records = (thinPass) => { for (const ch of p.strs) {
      for (let s = ch.first; s < ch.first + ch.count; s++) {
        const kind = str.kind[s];
        if (!!kindRow(kind).thin !== thinPass) continue;
        const f = str.srcKind[s] === SRC_BORES ? frames[str.srcIndex[s]] || null : null;
        const v0 = str.firstVert[s];
        const cache = f ? new Float32Array(str.nVert[s] * 5).fill(NaN) : null;
        // THE FILL STONE: a lit masonry / stone triangle (not a stair, not a light strip); its record's
        // fitted wall coordinates (stonewall.js wallCoords) measured on its first one
        const isStone = (t) => !(tflags[t] & (TF.NO_RENDER | TF.STAIR)) && STONE_MAT_IDX.has(mat[t]) && batchOf(kind, mat[t]) === 'lit'
          && !((tflags[t] & TF.LIGHT) && str.srcKind[s] === SRC_SITES);
        let wc = null;
        for (let t = str.firstTri[s]; t < str.firstTri[s] + str.nTri[s]; t++) {
          if (tflags[t] & TF.NO_RENDER) continue;
          const mr = matRow(mat[t]);
          const a = idx[t * 3] * 3, b = idx[t * 3 + 1] * 3, d = idx[t * 3 + 2] * 3;
          gt.push(t);
          if (batchOf(kind, mat[t]) === 'bore') {
            triMesh[t] = 1; triVert[t] = B.o;
            for (const v of [a, b, d]) {
              if (contact(v / 3)) foot(v / 3, B.o, fB);
              boreVert(verts[v], verts[v + 1], verts[v + 2], mr.tint, f, cache, v / 3 - v0);
            }
          } else {
            // lit, or (A3) the grate batch: a grating triangle
            const G = batchOf(kind, mat[t]) === 'grate', Q = G ? Gr : L;
            triMesh[t] = G ? 2 : 0; triVert[t] = Q.o;
            if (W !== null) {
              for (let k = 0; k < 3; k++) {
                const v = idx[t * 3 + k];
                if (contact(v)) foot(v, Q.o + k, G ? fG : fL);
              }
            }
            const stair = G ? kind === KF : tflags[t] & TF.STAIR;
            // (D1-TUNC R2) a grate triangle of a bore record: the bore light
            let cols = null, code = -1;
            if (G && f && DRAW.grate_bore_light.v) {
              cols = _cols;
              const al = mr.lin || (mr.lin = linOf(mr.color)), k = DRAW.bore_obj_k.v / mean * 255;
              [a, b, d].forEach((v, q) => {
                const E = boreE(verts[v], verts[v + 1], verts[v + 2], f, cache, v / 3 - v0);
                for (let c = 0; c < 3; c++) cols[q * 3 + c] = Math.min(255, Math.round(al[c] * E[c] * k));
              });
              code = stair ? GRATE_BORE_STAIR : GRATE_BORE_TRACK;
              nGrateBore++;
            }
            if (!G && (tflags[t] & TF.LIGHT) && str.srcKind[s] === SRC_SITES) {
              const w = DRAW.lamp_rgb.v.white;
              cols = [0, 1, 2].flatMap(() => w.map((c) => Math.round(255 * c)));
              code = LIGHT_CODE;
            } else if (!G && isStone(t)) code = STONE_CODE;
            let uvs = null;
            if (code === STONE_CODE) {
              // a wall face: its corners' (v, H); a top (or a face with no column height): 0, 0 -> plan stones
              uvs = ZERO_UV;
              if (steepTri(verts, idx, t)) {
                if (!wc) wc = wallCoords(str, s, isStone);
                const q = [a, b, d].flatMap((v) => [wc[(v / 3 - v0) * 2], wc[(v / 3 - v0) * 2 + 1]]);
                if (q[1] >= SW.min_h_m && q[3] >= SW.min_h_m && q[5] >= SW.min_h_m) uvs = q;
              }
            }
            litTri(Q, verts[a], verts[a + 1], verts[a + 2], verts[b], verts[b + 1], verts[b + 2],
              verts[d], verts[d + 1], verts[d + 2], code === STONE_CODE ? STONE_RGB : !G && stair && a5 ? a5 : mr.rgb || (mr.rgb = rgbOf(mr, mean)), stair, cols, code, uvs);
          }
        }
      }
    } };
    records(false);
    // rails over decks (lit, unchanged) / through bores (D1-TUNC: the
    // surface rail's look — ways2.js railUnit: steel running top, head
    // sides blending to the weathered foot — in a trapezoid section, lit by
    // the bore light of the frame each point lies in, entry.bf)
    for (const [r, i] of p.rails) {
      if (!r.bore[i]) {
        railSeg(r, i, (q) => {
          const rgb = lit.steel.rgb || (lit.steel.rgb = rgbOf(lit.steel, mean));
          litTri(L, q[0][0], q[0][1], q[0][2], q[1][0], q[1][1], q[1][2], q[2][0], q[2][1], q[2][2], rgb, 0);
        });
        continue;
      }
      const Ea = railLight(r, i), Eb = railLight(r, i + 1);
      railSeg(r, i, (q, f, ring, end) => {
        for (let k = 0; k < 3; k++) {
          const E = end[k] ? Eb : Ea, rb = ring[k] === 0 || ring[k] === 3;
          const c = rb ? RC.foot : f === 1 ? RC.top : RC.side;
          flatVert(B, q[k][0], q[k][1], q[k][2], c[0] * E[0] * OK, c[1] * E[1] * OK, c[2] * E[2] * OK, k);
        }
      }, DRAW.rail_head_w_m.v / 2, DRAW.rail_foot_w_m.v / 2);
    }
    // (D1-TUNC) THE CEILING LAMPS (owner-licensed invention 2026-10-11):
    // a luminaire at every lamp station (structplan boreLamps), its
    // underside glowing in the bore's lamp colour, its housing lit
    for (const Lp of p.lamps) {
      const f = boreF[Lp.k], hl = DRAW.fixture_len_m.v / 2, hw = DRAW.fixture_w_m.v / 2;
      const tx = Lp.tx, tz = Lp.tz, nx = -tz, nz = tx;
      const at = (a, b, y) => [Lp.x + tx * a * hl + nx * b * hw, y + a * hl * Lp.dy, Lp.z + tz * a * hl + nz * b * hw];   // pitched with the ceiling
      const gl = DRAW.fixture_glow.v / BM, G = [Lp.rgb[0] * gl, Lp.rgb[1] * gl, Lp.rgb[2] * gl];
      const E = f ? boreLight(f, Lp.s, boreCoord(f, Lp.x, Lp.y1, Lp.z).up, _E) : E0;
      const hk = DRAW.fixture_housing.v * OK, H = [E[0] * hk, E[1] * hk, E[2] * hk];
      const y0 = Lp.y0, y1 = Lp.y1;
      quad(B, at(-1, -1, y1), at(1, -1, y1), at(1, 1, y1), at(-1, 1, y1), G, G, G, G);            // the glow
      quad(B, at(-1, -1, y1), at(1, -1, y1), at(1, -1, y0), at(-1, -1, y0), H, H, H, H);          // long sides
      quad(B, at(-1, 1, y1), at(1, 1, y1), at(1, 1, y0), at(-1, 1, y0), H, H, H, H);
      quad(B, at(-1, -1, y1), at(-1, 1, y1), at(-1, 1, y0), at(-1, -1, y0), H, H, H, H);          // ends
      quad(B, at(1, -1, y1), at(1, 1, y1), at(1, 1, y0), at(1, -1, y0), H, H, H, H);
    }
    // the head: every vertex written before the thin kinds' tail
    const headL = L ? L.o : 0, headB = B ? B.o : 0, headG = Gr ? Gr.o : 0;
    records(true);
    // (bridge railings) the open railings: posts + rails (railing.js), thin
    for (const c of p.railings) {
      const S = DRAW.railing.v.styles[c.style], cp = S._rgb || (S._rgb = { post: rgbOf({ color: S.post_rgb }, mean), rail: rgbOf({ color: S.rail_rgb }, mean) });
      const n = railingGeom(c, DRAW.railing.v, (a, b, d, part) => litTri(L, a[0], a[1], a[2], b[0], b[1], b[2], d[0], d[1], d[2], cp[part], 0));
      if (n !== c.n) throw new Error(`structs2: railing of group ${p.cx},${p.cz} drew ${n} tris, the plan says ${c.n}`);
    }
    const tail = (L ? L.o - headL : 0) + (B ? B.o - headB : 0) + (Gr ? Gr.o - headG : 0);
    if (tail !== (p.n - p.nHead) * 3) throw new Error(`structs2: thin tail of group ${p.cx},${p.cz} is ${tail / 3} tris, the plan says ${p.n - p.nHead}`);
    // (D1-TUNC) THE NEAR SLEEPERS IN A BORE: every sleeper of the group
    // precomputed (TIE_V vertices: top + two long faces, lit by the bore
    // light, a per-sleeper weathering like ways2's), the block after the
    // batch: flat uv, parked on the first sleeper (degenerate) until update()
    // picks the ones within tie_view_m
    let ts = null;
    if (nTV) {
      const nG = p.ties.length, S = { pos: new Float32Array(nG * TIE_V * 3), col: new Float32Array(nG * TIE_V * 3), uv: null, o: 0 };
      const T = tieSrc.t, sw = DRAW.tie_w_m.v / 2, th = DRAW.tie_h_m.v;
      for (const i of p.ties) {
        const o = 8 * i, x = T[o], y = T[o + 1], z = T[o + 2], ax = T[o + 3], az = T[o + 4], hl = T[o + 5] / 2;
        const bx = az, bz = -ax;            // along the track
        const fi = tieSrc.f[i], f = fi >= 0 ? boreF[fi] : null;
        let E = E0;
        if (f) { const c = boreCoord(f, x, y, z); E = boreLight(f, c.s, c.up, _E); }
        const j = jit(x, z, 0.16) * OK;
        const C = [TIE_C[0] * E[0] * j, TIE_C[1] * E[1] * j, TIE_C[2] * E[2] * j], Cs = C.map((v) => v * TIE_SIDE);
        const at = (a, b, yy) => [x + ax * a * hl + bx * b * sw, yy, z + az * a * hl + bz * b * sw];
        quad(S, at(-1, -1, y), at(1, -1, y), at(1, 1, y), at(-1, 1, y), C, C, C, C);
        quad(S, at(-1, -1, y), at(1, -1, y), at(1, -1, y - th), at(-1, -1, y - th), Cs, Cs, Cs, Cs);
        quad(S, at(-1, 1, y), at(1, 1, y), at(1, 1, y - th), at(-1, 1, y - th), Cs, Cs, Cs, Cs);
      }
      const base = B.o;
      for (let v = 0; v < nTV; v++) {
        const o = base + v;
        B.pos[o * 3] = S.pos[0]; B.pos[o * 3 + 1] = S.pos[1]; B.pos[o * 3 + 2] = S.pos[2];
        B.uv[o * 2] = FLAT_U[v % 3]; B.uv[o * 2 + 1] = FLAT_V[v % 3];
      }
      // tiesNear's index of the group (structplan chunkPlan: tieXZ, tieBB, tieCells)
      ts = { base, cap: p.tieCap, pos: S.pos, col: S.col, tieXZ: p.tieXZ, tieBB: Float64Array.from(p.tieBB), tieCells: packTieCells(p.tieCells) };
      nTie += nG;
      tieBlock += p.tieCap;
    }
    const iL = nMesh, iB = iL + (L ? 1 : 0), iG = iB + (B ? 1 : 0);
    for (const t of gt) triMesh[t] = triMesh[t] === 1 ? iB : triMesh[t] === 2 ? iG : iL;
    // the lit and the grate batch: one attribute layout
    const litOut = (Q, n, head, tag) => {
      if (Q.o !== n * 3) throw new Error(`structs2: ${tag} batch of group ${p.cx},${p.cz} filled ${Q.o / 3} of ${n} tris`);
      return { pos: Q.pos, nor: Q.nor, col: Q.col, uv: Q.uv, st: Q.st, n, head, bs: sphereOf(Q.pos) };
    };
    let outL = null, outB = null, outG = null;
    if (L) { outL = litOut(L, nl, headL, 'lit'); nLit += nl; nMesh++; }
    if (B) {
      if (B.o !== nb * 3) throw new Error(`structs2: bore batch of group ${p.cx},${p.cz} filled ${B.o / 3} of ${nb} tris`);
      const bs = sphereOf(B.pos);
      // the sphere holds every sleeper the block can show
      if (ts) for (let k = 0; k < ts.pos.length; k += 3) expandSphere(bs, ts.pos[k], ts.pos[k + 1], ts.pos[k + 2]);
      outB = { pos: B.pos, col: B.col, uv: B.uv, n: nb, head: headB, bs };
      nBore += nb; nMesh++;
    }
    if (Gr) { outG = litOut(Gr, ng, headG, 'grate'); nGrate += ng; nMesh++; }
    let ft = null;
    if (fsrc.length) {
      const n = fsrc.length, fx = new Float32Array(n), fy = new Float32Array(n), fz = new Float32Array(n), f0 = new Float32Array(n);
      for (let u = 0; u < n; u++) {
        const v = fsrc[u];
        fx[u] = verts[v * 3]; fy[u] = verts[v * 3 + 1]; fz[u] = verts[v * 3 + 2];
        f0[u] = fy[u] + ground[v];
      }
      // per batch (0 lit, 1 bore, 2 grate) the written pairs and their vertex range (the upload range)
      const side = (k, has, list) => {
        if (!has || !list.length) return null;
        let lo = Infinity, hi = -1;
        for (let i = 0; i < list.length; i += 2) { if (list[i] < lo) lo = list[i]; if (list[i] > hi) hi = list[i]; }
        return { k, pairs: Uint32Array.from(list), lo, hi };
      };
      const fb = Uint8Array.from(fsrc, (v) => Bd[v]);
      ft = { x: fx, y: fy, z: fz, t0: f0, w: Float32Array.from(fsrc, (v) => W[v]), b: fb, sides: [side(0, L, fL), side(1, B, fB), side(2, Gr, fG)].filter(Boolean) };
      nFoot += n;
      for (let u = 0; u < n; u++) nBody += fb[u];
    }
    groups.push({ cx: p.cx, cz: p.cz, x: p.x, z: p.z, r: p.r, n: p.n, nHead: p.nHead, draws: p.draws, L: outL, B: outB, G: outG, ts, foot: ft });
  }
  const stats = { chunks: groups.length, meshes: nMesh, lit: nLit, bore: nBore, grate: nGrate, grate_bore: nGrateBore, rails: rails.length, framed: frames.filter(Boolean).length, contacts: nFoot, body: nBody,
    lamps: (rails.lamps || []).length, ties: nTie, tie_block: tieBlock };
  return { groups, triMesh, triVert, stats };
}
